/**
 * V208 / Phase E-1 — Customer picker page.
 *
 * Platform-Admin-only surface for browsing the customer roster and
 * switching the active session into a specific customer's scope. Built
 * to scale to large customer counts via:
 *   • search-as-you-type filter (client-side; the read endpoint is paged
 *     by the backend in the next iteration if N > a few thousand)
 *   • alphabetical segment chips (A–E, F–J, K–O, P–T, U–Z, 0–9, ALL)
 *   • status filter (ACTIVE / SUSPENDED / OFFBOARDED, multi-select)
 *
 * Clicking a customer card calls POST /api/v1/auth/switch-customer which
 * re-issues the JWT with the new `cid` claim. The page then reloads so
 * downstream surfaces re-fetch their scope-aware data.
 *
 * Onboard a new customer via the top-right "Onboard customer" button.
 * Renames + status changes live behind a per-row edit affordance (Phase D-1
 * PUT endpoint).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Search, Plus, RefreshCw, ChevronRight, AlertTriangle, Check, X,
  SlidersHorizontal, Archive, Pencil, Pause, Play,
} from 'lucide-react';

import { V2PageShell } from '../components/v2/V2PageShell';
import { Card } from '../components/v2/Card';
import { Button } from '../components/v2/Button';
import { Pill } from '../components/v2/Pill';
/* G589 — multi-step onboarding slideout replaces the prior 2-field
 * modal. Slideout drives the rich /onboard-with-topology endpoint,
 * which atomically provisions customer + topology + first admin. */
import { CustomerOnboardingSlideout } from '../components/v2/CustomerOnboardingSlideout';
import { EntitlementsPanel } from '../components/v2/EntitlementsPanel';
import { Slideout } from '../components/v2/Slideout';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../services/api';

const SEGMENTS = [
  { key: 'ALL', label: 'All',   matches: () => true },
  { key: 'A-E', label: 'A–E',   matches: (n) => /^[a-eA-E]/.test(n) },
  { key: 'F-J', label: 'F–J',   matches: (n) => /^[f-jF-J]/.test(n) },
  { key: 'K-O', label: 'K–O',   matches: (n) => /^[k-oK-O]/.test(n) },
  { key: 'P-T', label: 'P–T',   matches: (n) => /^[p-tP-T]/.test(n) },
  { key: 'U-Z', label: 'U–Z',   matches: (n) => /^[u-zU-Z]/.test(n) },
  { key: '0-9', label: '0–9',   matches: (n) => /^[0-9]/.test(n) },
];

const STATUS_TONE = {
  ACTIVE:     'success',
  SUSPENDED:  'warning',
  OFFBOARDED: 'muted',
};

export default function CustomersPageV2() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  /* G583 — pull switchCustomer from AuthContext (not from api.js direct).
   * The context-mediated path persists the new JWT to localStorage and
   * fires _onTokenRefreshed so the apiFetch helper picks up the fresh
   * token before the page reloads. Without this the next request after
   * window.location.href used the stale token and JwtAuthFilter forced
   * a re-login with reason=role_changed. */
  const { session, switchCustomer, currentRole } = useAuth();
  /* DEF-FE-CUSTOMER-GATE-001 — customer onboard is Platform Admin only.
   * isSuperAdmin() is the wildcard-tier check and returns TRUE for both
   * Platform Admin and Super Admin, which would still leak the button to
   * SA. Gate strictly on currentRole === 'Platform Admin' instead. Backend
   * (CustomerOnboardingService) is the authoritative gate; this is the
   * UX-layer hide that prevents SA from reaching a button that will only
   * 403. */
  const isPlatformAdmin = currentRole === 'Platform Admin';

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [segment, setSegment] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState(new Set(['ACTIVE']));
  const [switching, setSwitching] = useState(null); // customer id currently being switched into
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [planCustomer, setPlanCustomer] = useState(null);
  /* DEF-FE-CUSTOMER-OFFBOARD-001 — UI surface for soft-deleting a customer.
   * Backend DELETE /api/v1/customers/{id} (api.offboardCustomer) and the
   * CUSTOMER_OFFBOARDED audit event already exist; only the UI affordance
   * was missing. Like onboard, this is PA-only (CustomerOnboardingService
   * enforces). The type-to-confirm modal pattern matches the Multi-Tenancy
   * tenant Delete + Agent Delete dialogs for muscle-memory consistency. */
  const [offboardCandidate, setOffboardCandidate] = useState(null); // customer obj or null
  const [offboardConfirmText, setOffboardConfirmText] = useState('');
  const [offboarding, setOffboarding] = useState(false);
  const [offboardError, setOffboardError] = useState(null);
  /* View/Edit — edits SAFE fields only (display name). Never touches the
   * canonical name, org/tenant structure, role grants, or executions. */
  const [editCustomer, setEditCustomer] = useState(null);
  const [editForm, setEditForm] = useState({ displayName: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const openEdit = (c) => { setEditCustomer(c); setEditForm({ displayName: c.displayName || c.name || '' }); setEditError(null); };
  /* Suspend/Reactivate — reversible status flip (distinct from Offboard).
   * { customer, action: 'SUSPENDED' | 'ACTIVE' }. */
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState(null);

  // ── Initial load + refresh ────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listCustomers();
      if (res && res.ok === false) {
        setError(res.error || 'Failed to load customers');
        setCustomers([]);
      } else {
        const payload = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
        setCustomers(Array.isArray(payload) ? payload : []);
      }
    } catch (e) {
      setError(e?.message || String(e));
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ── Filter pipeline ────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const seg = SEGMENTS.find((s) => s.key === segment) || SEGMENTS[0];
    const q = query.trim().toLowerCase();
    return customers.filter((c) => {
      if (!statusFilter.has(c.status)) return false;
      const name = (c.displayName || c.name || '').toString();
      if (!seg.matches(name)) return false;
      if (q && !(name.toLowerCase().includes(q) || (c.name || '').toLowerCase().includes(q))) {
        return false;
      }
      return true;
    });
  }, [customers, query, segment, statusFilter]);

  // ── Switch into customer ──────────────────────────────────────────
  const handleSwitch = async (customer) => {
    if (customer.status !== 'ACTIVE') {
      addToast({
        title: 'Cannot switch',
        message: `${customer.displayName || customer.name} is ${customer.status}. Reactivate first.`,
        tone: 'warning',
      });
      return;
    }
    setSwitching(customer.id);
    try {
      // G583 — go through AuthContext so the freshly-issued JWT lands
      // in localStorage BEFORE the page reloads. Calling api.switchCustomer
      // directly would have thrown the new token away and left the next
      // request authenticating with the now-blacklisted old one.
      const res = await switchCustomer(customer.id);
      if (res && res.ok === false) {
        addToast({
          title: 'Switch failed',
          message: res.error || 'Unable to switch customer scope',
          tone: 'danger',
        });
        setSwitching(null);
        return;
      }
      addToast({
        title: 'Switched',
        message: `Now scoped to ${customer.displayName || customer.name}`,
        tone: 'success',
      });
      // Reload to re-resolve every page against the new scope. Mirrors
      // switch-membership / switch-instance pattern in DashboardLayoutV2.
      window.location.href = '/overview';
    } catch (e) {
      addToast({
        title: 'Switch failed',
        message: e?.message || String(e),
        tone: 'danger',
      });
      setSwitching(null);
    }
  };

  // Onboarding is handled by CustomerOnboardingSlideout (multi-step /onboard-with-topology).

  // Toggle status filter chip
  const toggleStatus = (s) => {
    const next = new Set(statusFilter);
    if (next.has(s)) next.delete(s); else next.add(s);
    // Don't allow ALL-off — would always show empty.
    if (next.size === 0) return;
    setStatusFilter(next);
  };

  const activeCustomerId = session?.customerId;

  return (
    <V2PageShell
      title="Customers"
      subtitle="Switch the active session into a specific customer. Platform Admin only."
      backTo="/overview"
      backLabel="Back to Overview"
      tags={<Pill tone="action" size="sm">Platform</Pill>}
      actions={
        <>
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<RefreshCw size={14} />}
            onClick={load}
            disabled={loading}
          >Refresh</Button>
          {isPlatformAdmin && (
            <Button
              variant="primary"
              size="sm"
              iconLeft={<Plus size={14} />}
              onClick={() => setOnboardOpen(true)}
            >Onboard customer</Button>
          )}
        </>
      }
    >
      {/* ── Filter bar ────────────────────────────────────────────── */}
      <Card padding="sm" className="mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[240px] bg-v2-muted h-9 px-3 rounded-v2-sm border border-v2-border-default">
            <Search size={14} aria-hidden="true" className="text-v2-ink-muted" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name…"
              aria-label="Search customers"
              className="flex-1 bg-transparent border-0 outline-none text-[12.5px] text-v2-ink placeholder:text-v2-ink-muted"
            />
            {query && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setQuery('')}
                className="text-v2-ink-muted hover:text-v2-ink"
              ><X size={12} /></button>
            )}
          </div>
          {/* Status chips */}
          <div className="flex items-center gap-1.5">
            {Object.keys(STATUS_TONE).map((s) => {
              const active = statusFilter.has(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleStatus(s)}
                  aria-pressed={active}
                  className={
                    'v2-focus-ring h-7 px-2.5 rounded-v2-pill text-[11px] font-medium transition-colors duration-v2 '
                    + (active
                      ? 'bg-v2-action-soft text-v2-action border border-v2-action'
                      : 'bg-v2-muted text-v2-ink-muted border border-v2-border-default hover:bg-v2-elevated')
                  }
                >{s}</button>
              );
            })}
          </div>
        </div>
        {/* Segment chips */}
        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          {SEGMENTS.map((s) => {
            const active = segment === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setSegment(s.key)}
                aria-pressed={active}
                className={
                  'v2-focus-ring h-7 px-2.5 rounded-v2-pill text-[11px] font-semibold transition-colors duration-v2 '
                  + (active
                    ? 'bg-v2-ink text-v2-ink-inverse'
                    : 'bg-v2-muted text-v2-ink-secondary hover:bg-v2-elevated')
                }
              >{s.label}</button>
            );
          })}
        </div>
      </Card>

      {/* ── Results ───────────────────────────────────────────────── */}
      {error && (
        <Card padding="sm" tone="danger" className="mb-4">
          <div className="flex items-center gap-2 text-v2-danger text-[13px]">
            <AlertTriangle size={14} />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {loading && (
        <Card padding="md">
          <div className="text-v2-ink-muted text-[13px]">Loading customers…</div>
        </Card>
      )}

      {!loading && filtered.length === 0 && (
        <Card padding="lg">
          <div className="text-center">
            <Building2 size={32} className="mx-auto text-v2-ink-muted mb-2" aria-hidden="true" />
            <div className="text-[14px] font-semibold text-v2-ink">No customers match these filters</div>
            <div className="text-[12px] text-v2-ink-muted mt-1">
              {customers.length === 0
                ? 'No customers exist yet. Onboard the first one to get started.'
                : 'Try clearing the search or expanding the status / segment chips.'}
            </div>
          </div>
        </Card>
      )}

      {!loading && filtered.length > 0 && (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px] border-collapse">
              <thead>
                <tr>
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-v2-ink-muted border-b border-v2-border-subtle">Customer</th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-v2-ink-muted border-b border-v2-border-subtle">Status</th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-v2-ink-muted border-b border-v2-border-subtle">Onboarded</th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-v2-ink-muted border-b border-v2-border-subtle w-[1%] whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const isActive = c.id === activeCustomerId;
                  const isInactive = c.status !== 'ACTIVE';
                  return (
                    <tr
                      key={c.id}
                      className={'border-b border-v2-border-subtle last:border-b-0 '
                        + (isActive ? 'bg-v2-action-soft' : 'hover:bg-v2-elevated')}
                    >
                      {/* Customer — avatar + display name + canonical name + active-scope pill */}
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-3">
                          <div
                            aria-hidden="true"
                            className="w-9 h-9 rounded-v2-md flex items-center justify-center font-bold text-[13px] bg-v2-action-soft text-v2-action flex-shrink-0"
                          >{(c.displayName || c.name).slice(0, 2).toUpperCase()}</div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[13.5px] font-semibold text-v2-ink truncate">{c.displayName || c.name}</span>
                              {isActive && <Pill tone="action" size="sm" dot>Active scope</Pill>}
                            </div>
                            <div className="text-[11px] text-v2-ink-muted font-mono truncate">{c.name}</div>
                          </div>
                        </div>
                      </td>
                      {/* Status */}
                      <td className="px-4 py-2.5">
                        <Pill tone={STATUS_TONE[c.status] || 'muted'} size="sm">{c.status}</Pill>
                      </td>
                      {/* Onboarded */}
                      <td className="px-4 py-2.5 text-v2-ink-muted whitespace-nowrap">
                        {c.onboardedAt ? new Date(c.onboardedAt).toLocaleDateString() : '—'}
                      </td>
                      {/* Actions — fixed Switch-into slot + equal icon slots so every row aligns in columns.
                        * Handlers/gating/titles are identical to the prior layout; only alignment changed. */}
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {/* Primary: fixed-width slot so every "Switch into" starts at the same x */}
                          <div className="w-[140px] flex-shrink-0">
                            <Button
                              variant={isActive ? 'secondary' : 'primary'}
                              size="sm"
                              disabled={isActive || isInactive || switching === c.id}
                              onClick={() => handleSwitch(c)}
                              iconRight={!isActive ? <ChevronRight size={12} /> : <Check size={12} />}
                            >
                              {isActive ? 'Current scope' : (switching === c.id ? 'Switching…' : 'Switch into')}
                            </Button>
                          </div>
                          {/* Secondary icon actions — four equal slots (Plan · Edit · Suspend/Reactivate · Offboard);
                            * an inapplicable action renders an empty slot so the icons stay column-aligned. */}
                          {isPlatformAdmin && (
                            <div className="flex items-center">
                              <div className="w-10 flex justify-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setPlanCustomer(c)}
                                  aria-label="Manage plan & entitlements"
                                  title="Plan & entitlements"
                                ><SlidersHorizontal size={15} /></Button>
                              </div>
                              <div className="w-10 flex justify-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEdit(c)}
                                  aria-label="Edit customer display name"
                                  title="Edit display name"
                                ><Pencil size={15} /></Button>
                              </div>
                              {/* Suspend | Reactivate | empty (mutually exclusive; legacy excluded) */}
                              <div className="w-10 flex justify-center">
                                {c.status === 'ACTIVE' && !isActive && c.name !== 'legacy' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setStatusTarget({ customer: c, action: 'SUSPENDED' }); setStatusError(null); }}
                                    className="!text-v2-warning hover:!bg-v2-warning-soft"
                                    aria-label="Suspend customer"
                                    title="Suspend (reversible)"
                                  ><Pause size={15} /></Button>
                                )}
                                {c.status === 'SUSPENDED' && c.name !== 'legacy' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setStatusTarget({ customer: c, action: 'ACTIVE' }); setStatusError(null); }}
                                    className="!text-v2-success hover:!bg-v2-success-soft"
                                    aria-label="Reactivate customer"
                                    title="Reactivate"
                                  ><Play size={15} /></Button>
                                )}
                              </div>
                              {/* Offboard | empty — PA-only, hidden on offboarded + on the active scope. */}
                              <div className="w-10 flex justify-center">
                                {c.status === 'ACTIVE' && !isActive && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setOffboardCandidate(c); setOffboardConfirmText(''); setOffboardError(null); }}
                                    className="!text-v2-danger hover:!bg-v2-danger-soft"
                                    aria-label="Offboard customer"
                                    title="Offboard (soft-delete, audit preserved)"
                                  ><Archive size={15} /></Button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── G589 Onboarding slideout ───────────────────────────────
        * Multi-step: Customer → Topology → Admin → Review → Result.
        * onCreated fires after a successful /onboard-with-topology;
        * we reload the customer list so the new row appears
        * immediately when the operator closes the slideout. */}
      <CustomerOnboardingSlideout
        isOpen={onboardOpen}
        onClose={() => setOnboardOpen(false)}
        onCreated={() => { load(); }}
      />

      <EntitlementsPanel
        isOpen={!!planCustomer}
        customer={planCustomer}
        onClose={() => setPlanCustomer(null)}
        onChanged={() => { load(); }}
      />

      {/* DEF-FE-CUSTOMER-OFFBOARD-001 — Offboard confirmation (Slideout · V2 inline-over-modal) */}
      <Slideout
        isOpen={!!offboardCandidate}
        onClose={() => setOffboardCandidate(null)}
        ariaLabel="Offboard customer"
        width={480}
        header={offboardCandidate && (
          <div className="text-[15px] font-semibold text-v2-ink">
            Offboard {offboardCandidate.displayName || offboardCandidate.name}?
          </div>
        )}
        footer={offboardCandidate && (
          <>
            <Button variant="ghost" size="sm" onClick={() => setOffboardCandidate(null)} disabled={offboarding}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              disabled={offboarding || offboardConfirmText.trim() !== offboardCandidate.name}
              onClick={async () => {
                setOffboarding(true);
                setOffboardError(null);
                try {
                  const res = await api.offboardCustomer(offboardCandidate.id);
                  if (res && res.ok === false) {
                    setOffboardError(res.error || 'Offboard failed');
                  } else {
                    addToast({
                      title: 'Customer offboarded',
                      message: `${offboardCandidate.displayName || offboardCandidate.name} is now OFFBOARDED`,
                      tone: 'warning',
                    });
                    setOffboardCandidate(null);
                    load();
                  }
                } catch (e) {
                  setOffboardError(e?.message || String(e));
                } finally {
                  setOffboarding(false);
                }
              }}
              className="!bg-v2-danger !text-v2-ink-inverse hover:!bg-v2-danger-strong"
            >
              {offboarding ? 'Offboarding…' : 'Offboard'}
            </Button>
          </>
        )}
      >
        {offboardCandidate && (
          <div>
            <div className="text-[13px] text-v2-ink-secondary mb-3">
              This soft-deletes the customer (status flips to OFFBOARDED, <code>offboarded_at</code> set to now).
              A <code>CUSTOMER_OFFBOARDED</code> row lands on the audit chain. Orgs and tenants under this customer
              remain in-place but new operations against them will 403.
              <span className="block mt-2 text-v2-danger font-medium">
                Permanent: single-scoped users of this customer are deleted (their accounts removed; group
                memberships, agent assignments, and approval history stay in the audit trail). Users who also belong
                to another customer keep their account and lose only this customer&apos;s access. Offboarding cannot be
                undone — a returning customer must be onboarded fresh.
              </span>
            </div>
            <div className="text-[12.5px] text-v2-ink-muted mb-2">
              Type <code className="font-mono text-v2-ink">{offboardCandidate.name}</code> to confirm:
            </div>
            <input
              type="text"
              value={offboardConfirmText}
              onChange={(e) => setOffboardConfirmText(e.target.value)}
              autoFocus
              className="w-full h-9 px-3 rounded-v2-sm border border-v2-border-default bg-v2-muted text-[13px] text-v2-ink mb-3"
              placeholder={offboardCandidate.name}
            />
            {offboardError && (
              <div className="text-[12px] text-v2-danger mb-2">{offboardError}</div>
            )}
          </div>
        )}
      </Slideout>
      {/* View / Edit customer — safe fields only (Slideout · V2 inline-over-modal) */}
      <Slideout
        isOpen={!!editCustomer}
        onClose={() => setEditCustomer(null)}
        ariaLabel="Customer details"
        width={480}
        header={<div className="text-[15px] font-semibold text-v2-ink">Customer details</div>}
        footer={editCustomer && (
          <>
            <Button variant="ghost" size="sm" onClick={() => setEditCustomer(null)} disabled={editSaving}>Close</Button>
            <Button
              variant="primary"
              size="sm"
              disabled={editSaving || editCustomer.name === 'legacy' || !editForm.displayName.trim() || editForm.displayName.trim() === (editCustomer.displayName || editCustomer.name)}
              onClick={async () => {
                setEditSaving(true); setEditError(null);
                try {
                  const res = await api.updateCustomer(editCustomer.id, { displayName: editForm.displayName.trim() });
                  if (res && res.ok === false) { setEditError(res.error || res.data?.error || 'Update failed'); }
                  else { addToast({ title: 'Customer updated', tone: 'success' }); setEditCustomer(null); load(); }
                } catch (e) { setEditError(e?.message || String(e)); } finally { setEditSaving(false); }
              }}
            >{editSaving ? 'Saving…' : 'Save changes'}</Button>
          </>
        )}
      >
        {editCustomer && (
          <div>
            <div className="space-y-1.5 text-[12.5px] mb-3">
              <div className="flex items-center justify-between"><span className="text-v2-ink-muted">Canonical name</span><span className="font-mono text-v2-ink">{editCustomer.name}</span></div>
              <div className="flex items-center justify-between"><span className="text-v2-ink-muted">Status</span><Pill tone={STATUS_TONE[editCustomer.status] || 'muted'} size="sm">{editCustomer.status}</Pill></div>
              {editCustomer.onboardedAt && (
                <div className="flex items-center justify-between"><span className="text-v2-ink-muted">Onboarded</span><span className="text-v2-ink">{new Date(editCustomer.onboardedAt).toLocaleDateString()}</span></div>
              )}
            </div>
            <div className="text-[12px] text-v2-ink-muted mb-1">
              Display name{editCustomer.name === 'legacy' && <span className="ml-1">(system row — read-only)</span>}
            </div>
            <input
              type="text"
              value={editForm.displayName}
              disabled={editSaving || editCustomer.name === 'legacy'}
              onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
              className="w-full h-9 px-3 rounded-v2-sm border border-v2-border-default bg-v2-muted text-[13px] text-v2-ink mb-1"
            />
            <div className="text-[11px] text-v2-ink-muted mb-3">Only the display name is editable here. Canonical name, org/tenant structure, role grants, and executions are never changed.</div>
            {editError && <div className="text-[12px] text-v2-danger mb-2">{editError}</div>}
          </div>
        )}
      </Slideout>

      {/* Suspend / Reactivate — reversible status flip (Slideout · V2 inline-over-modal) */}
      <Slideout
        isOpen={!!statusTarget}
        onClose={() => setStatusTarget(null)}
        ariaLabel="Change customer status"
        width={460}
        header={statusTarget && (
          <div className="text-[15px] font-semibold text-v2-ink">
            {statusTarget.action === 'SUSPENDED' ? 'Suspend' : 'Reactivate'} {statusTarget.customer.displayName || statusTarget.customer.name}?
          </div>
        )}
        footer={statusTarget && (
          <>
            <Button variant="ghost" size="sm" onClick={() => setStatusTarget(null)} disabled={statusSaving}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              disabled={statusSaving}
              className={statusTarget.action === 'SUSPENDED' ? '!bg-v2-warning !text-v2-ink-inverse' : ''}
              onClick={async () => {
                setStatusSaving(true); setStatusError(null);
                try {
                  const res = await api.updateCustomer(statusTarget.customer.id, { status: statusTarget.action });
                  if (res && res.ok === false) { setStatusError(res.error || res.data?.error || 'Status change failed'); }
                  else {
                    addToast({ title: statusTarget.action === 'SUSPENDED' ? 'Customer suspended' : 'Customer reactivated', tone: statusTarget.action === 'SUSPENDED' ? 'warning' : 'success' });
                    setStatusTarget(null); load();
                  }
                } catch (e) { setStatusError(e?.message || String(e)); } finally { setStatusSaving(false); }
              }}
            >{statusSaving ? 'Working…' : (statusTarget.action === 'SUSPENDED' ? 'Suspend' : 'Reactivate')}</Button>
          </>
        )}
      >
        {statusTarget && (
          <div className="text-[13px] text-v2-ink-secondary">
            {statusTarget.action === 'SUSPENDED'
              ? <>Sets status to <code>SUSPENDED</code> (reversible). The customer&apos;s orgs, tenants, and data are preserved. Single-scoped users are suspended and signed out immediately — they can&apos;t log in until reactivation. Users who also belong to another active customer keep their account; only their session re-scopes. No structural or RBAC changes.</>
              : <>Sets status back to <code>ACTIVE</code>. The customer&apos;s scope becomes operable again, and the users this suspension auto-suspended are restored to active.</>}
            {statusError && <div className="text-[12px] text-v2-danger mt-2">{statusError}</div>}
          </div>
        )}
      </Slideout>
    </V2PageShell>
  );
}
